import { useMutation, useQueryClient } from '@tanstack/react-query';
import { authenticatedApiClient, apiClient } from '../api/client';
import { useCustomQuery } from './useCustomQuery';
import type { ObjectMember, MembershipStatus, MembershipRole } from '@sizzlenz/shared';

// --- Types ---

export interface MembershipResponse {
  members: ObjectMember[];
  totalCount: number;
  pagination: {
    nextCursor: string | null;
    hasNextPage: boolean;
    limit: number;
  };
  currentUserMembership: {
    id: number;
    role: MembershipRole;
    status: MembershipStatus;
    joinedAt: string;
  } | null;
  settings: {
    showMemberCount: boolean;
    capacity: number | null;
    joinLabel?: string;
    leaveLabel?: string;
    autoApprove: boolean;
  };
}

export interface MembershipStatusResponse {
  membership: {
    id: number;
    role: MembershipRole;
    status: MembershipStatus;
    joinedAt: string;
  } | null;
}

interface JoinResponse {
  membership: {
    id: number;
    role: MembershipRole;
    status: MembershipStatus;
    joinedAt: string | null;
  };
  message: string;
}

// --- Query keys ---

export const membershipKeys = {
  all: ['membership'] as const,
  members: (objectType: string, objectId: number) =>
    [...membershipKeys.all, 'members', objectType, objectId] as const,
  status: (objectType: string, objectId: number) =>
    [...membershipKeys.all, 'status', objectType, objectId] as const,
  pending: (objectType: string, objectId: number) =>
    [...membershipKeys.all, 'pending', objectType, objectId] as const,
};

// --- API functions ---

export const membershipApi = {
  getMembers: (
    objectType: string,
    objectId: number,
    params?: {
      status?: string;
      role?: string;
      limit?: number;
      cursor?: string;
      order?: 'asc' | 'desc';
    }
  ) => {
    const searchParams: any = {};
    if (params?.status) searchParams.status = params.status;
    if (params?.role) searchParams.role = params.role;
    if (params?.limit) searchParams.limit = params.limit.toString();
    if (params?.cursor) searchParams.cursor = params.cursor;
    if (params?.order) searchParams.order = params.order;
    return apiClient
      .get(`membership/${objectType}/${objectId}`, { searchParams })
      .json<MembershipResponse>();
  },

  getStatus: (objectType: string, objectId: number) =>
    apiClient.get(`membership/${objectType}/${objectId}/status`).json<MembershipStatusResponse>(),

  join: (objectType: string, objectId: number) =>
    authenticatedApiClient
      .post(`membership/${objectType}/${objectId}/join`, { json: {} })
      .json<JoinResponse>(),

  leave: (objectType: string, objectId: number) =>
    authenticatedApiClient
      .post(`membership/${objectType}/${objectId}/leave`, { json: {} })
      .json<{ message: string }>(),

  updateMember: (
    objectType: string,
    objectId: number,
    memberId: number,
    status: MembershipStatus
  ) =>
    authenticatedApiClient
      .patch(`membership/${objectType}/${objectId}/members/${memberId}`, {
        json: { status },
      })
      .json<{
        membership: { id: number; role: MembershipRole; status: MembershipStatus };
        message: string;
      }>(),

  changeRole: (objectType: string, objectId: number, memberId: number, role: MembershipRole) =>
    authenticatedApiClient
      .patch(`membership/${objectType}/${objectId}/members/${memberId}`, {
        json: { role },
      })
      .json<{
        membership: { id: number; role: MembershipRole; status: MembershipStatus };
        message: string;
      }>(),

  removeMember: (objectType: string, objectId: number, memberId: number) =>
    authenticatedApiClient
      .delete(`membership/${objectType}/${objectId}/members/${memberId}`, { json: {} })
      .json<{ message: string }>(),

  updateBio: (objectType: string, objectId: number, bio: string) =>
    authenticatedApiClient
      .patch(`membership/${objectType}/${objectId}/bio`, { json: { bio } })
      .json<{ bio: string }>(),
};

// --- Hooks ---

/**
 * Hook to fetch members of an object with the current user's membership status.
 * Pass status as comma-separated string to include multiple: 'active,pending'
 */
export function useObjectMembers(
  objectType: string,
  objectId: number,
  options?: {
    status?: string;
    role?: string;
    limit?: number;
    order?: 'asc' | 'desc';
    enabled?: boolean;
  }
) {
  return useCustomQuery({
    queryKey: [
      ...membershipKeys.members(objectType, objectId),
      options?.status,
      options?.role,
      options?.order,
    ],
    queryFn: () =>
      membershipApi.getMembers(objectType, objectId, {
        status: options?.status,
        role: options?.role,
        limit: options?.limit,
        order: options?.order,
      }),
    enabled: options?.enabled !== false && !!objectType && !!objectId,
    staleTime: 1000 * 10,
  });
}

/**
 * Hook to fetch pending members (for admin approval view).
 */
export function usePendingMembers(
  objectType: string,
  objectId: number,
  options?: { enabled?: boolean }
) {
  return useCustomQuery({
    queryKey: membershipKeys.pending(objectType, objectId),
    queryFn: () => membershipApi.getMembers(objectType, objectId, { status: 'pending' }),
    enabled: options?.enabled !== false && !!objectType && !!objectId,
    staleTime: 1000 * 10,
  });
}

/**
 * Hook to fetch the current user's membership status for an object.
 */
export function useMembershipStatus(
  objectType: string,
  objectId: number,
  options?: { enabled?: boolean }
) {
  return useCustomQuery({
    queryKey: membershipKeys.status(objectType, objectId),
    queryFn: () => membershipApi.getStatus(objectType, objectId),
    enabled: options?.enabled !== false && !!objectType && !!objectId,
    staleTime: 1000 * 10,
  });
}

/**
 * Hook for join/leave mutations with optimistic updates.
 */
export function useMembershipActions(objectType: string, objectId: number) {
  const queryClient = useQueryClient();

  const invalidateAll = () => {
    // Only invalidate the members query — it returns currentUserMembership and all needed data
    queryClient.invalidateQueries({ queryKey: membershipKeys.members(objectType, objectId) });
  };

  const joinMutation = useMutation({
    mutationFn: () => membershipApi.join(objectType, objectId),
    onSuccess: () => invalidateAll(),
  });

  const leaveMutation = useMutation({
    mutationFn: () => membershipApi.leave(objectType, objectId),
    onSuccess: () => invalidateAll(),
  });

  const approveMutation = useMutation({
    mutationFn: (memberId: number) =>
      membershipApi.updateMember(objectType, objectId, memberId, 'active'),
    onSuccess: () => invalidateAll(),
  });

  const rejectMutation = useMutation({
    mutationFn: (memberId: number) =>
      membershipApi.updateMember(objectType, objectId, memberId, 'rejected'),
    onSuccess: () => invalidateAll(),
  });

  const removeMutation = useMutation({
    mutationFn: (memberId: number) => membershipApi.removeMember(objectType, objectId, memberId),
    onSuccess: () => invalidateAll(),
  });

  const changeRoleMutation = useMutation({
    mutationFn: ({ memberId, role }: { memberId: number; role: MembershipRole }) =>
      membershipApi.changeRole(objectType, objectId, memberId, role),
    onSuccess: () => invalidateAll(),
  });

  const updateBioMutation = useMutation({
    mutationFn: (bio: string) => membershipApi.updateBio(objectType, objectId, bio),
    onSuccess: () => invalidateAll(),
  });

  return {
    join: joinMutation,
    leave: leaveMutation,
    approve: approveMutation,
    reject: rejectMutation,
    remove: removeMutation,
    changeRole: changeRoleMutation,
    updateBio: updateBioMutation,
  };
}
